import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Drawer, Collapse, List, Typography, Tag, Empty, Spin, App, Input, Button, Segmented } from 'antd';
import { ClockCircleOutlined, DollarOutlined, ShoppingCartOutlined, SearchOutlined, FolderOpenOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  fetchProductImages,
  fetchSharedResult,
  fetchUserResults,
  logRequestFailure,
  type HistoryGroup,
  type ProductImages,
} from '../../shared/api/client';
import { parseShareIdInput } from '../../shared/lib/platformShareLink';
import { useAuthStore } from '../../shared/session/authStore';
import ProductThumb from '../../shared/ui/ProductThumb';
import HistoryTable from './HistoryTable';
import { exchangeGroupImages, exchangeXidFromKey, flattenRows } from './historyTableLogic';
import { isAsin } from '../../shared/lib/asin';

const THUMB_SIZE = 40;

/** Список — быстрый выбор версии; таблица — сравнение расчётов по метрикам. */
export type HistoryMode = 'list' | 'table';

interface HistoryHeadingProps {
  mode: HistoryMode;
  onModeChange: (mode: HistoryMode) => void;
}

/** Заголовок истории с переключателем вида — один и тот же в шторке и на вкладке. */
export const HistoryHeading: React.FC<HistoryHeadingProps> = ({ mode, onModeChange }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuthStore();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      <span>
        {t('idea_calc.history.title')}
        {isAdmin && <Tag color="red" style={{ marginLeft: 8 }}>Admin</Tag>}
      </span>
      <Segmented<HistoryMode>
        size="small"
        value={mode}
        onChange={onModeChange}
        options={[
          { value: 'list', label: t('idea_calc.history.mode_list') },
          { value: 'table', label: t('idea_calc.history.mode_table') },
        ]}
      />
    </span>
  );
};

interface HistoryPanelProps {
  /**
   * История на экране. Список перечитывается при каждом переходе в true: расчёт
   * могли сохранить, пока история была скрыта.
   */
  active: boolean;
  mode: HistoryMode;
  onLoadResult: (fileName: string) => void;
  /**
   * Загрузка расчёта по share-id (или ссылке) из поля поиска — для чужих
   * расчётов, которых в своей истории нет. Данные уже получены, владелец
   * панели их только применяет.
   */
  onLoadShared: (data: Record<string, unknown>) => void;
  /** Адрес расчёта по id — у имени в таблице появляется настоящая ссылка (см. HistoryTable). */
  resultHref?: (resultId: number) => string;
}

/** Поиск, загрузка по share-id и сама история (список или таблица) — без обёртки. */
export const HistoryPanel: React.FC<HistoryPanelProps> = ({ active, mode, onLoadResult, onLoadShared, resultHref }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [groups, setGroups] = useState<HistoryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingShared, setLoadingShared] = useState(false);
  const [images, setImages] = useState<ProductImages>({});
  // ASIN, за фото которых уже сходили в это открытие панели: без этого каждая
  // перерисовка до ответа слала бы тот же батч заново, а отказ — зацикливал запросы.
  const requestedImages = useRef(new Set<string>());

  useEffect(() => {
    if (!active) return;
    requestedImages.current.clear();
    setSearch('');
    setLoading(true);
    fetchUserResults()
      .then((res) => setGroups(res.groups))
      .catch(() => message.error(t('idea_calc.history.msg_load_error')))
      .finally(() => setLoading(false));
  }, [active]);

  const query = search.trim().replace(/\.json$/i, '').toLowerCase();

  // В реальной базе тысячи расчётов — рендерим не больше MAX_RENDERED_GROUPS
  // групп за раз, иначе Collapse кладёт в DOM тысячи узлов и панель тормозит.
  const MAX_RENDERED_GROUPS = 100;

  const filteredGroups = useMemo(() => {
    if (!query) return groups;
    return groups
      .map((group) => {
        if (group.asin.toLowerCase().includes(query)) return group;
        const versions = group.versions.filter((v) => v.fileName.toLowerCase().includes(query));
        if (versions.length === 0) return null;
        return { ...group, versions, versions_count: versions.length };
      })
      .filter((g): g is HistoryGroup => g !== null);
  }, [groups, query]);

  const visibleGroups = useMemo(() => filteredGroups.slice(0, MAX_RENDERED_GROUPS), [filteredGroups]);
  const tableRows = useMemo(() => flattenRows(filteredGroups), [filteredGroups]);
  const hiddenCount = filteredGroups.length - visibleGroups.length;
  const shownImages = useMemo(() => ({ ...images, ...exchangeGroupImages(groups) }), [images, groups]);

  // Фото — только для отрисованных групп: промах кэша на бэкенде стоит токен
  // Keepa за ASIN, а у админа в истории тысячи товаров.
  useEffect(() => {
    if (!active) return;
    // Бэкенд отвергает весь батч фото, если в нём есть не-ASIN, — такие ключи групп не шлём.
    const missing = visibleGroups
      .map((group) => group.asin)
      .filter((asin) => isAsin(asin) && !(asin in images) && !requestedImages.current.has(asin));
    if (missing.length === 0) return;
    missing.forEach((asin) => requestedImages.current.add(asin));
    fetchProductImages(missing)
      .then((loaded) => setImages((prev) => ({ ...prev, ...loaded })))
      // Фото — не данные расчёта: при отказе строка остаётся с рамкой-заглушкой,
      // причина уходит в консоль, повтор — при следующем открытии панели.
      .catch((error: unknown) => logRequestFailure('Product images load error:', error));
  }, [active, visibleGroups, images]);

  const handleLoad = (fileName: string) => {
    onLoadResult(fileName);
  };

  const shareIdInput = parseShareIdInput(search);

  const handleLoadShared = async () => {
    if (!shareIdInput) return;
    setLoadingShared(true);
    try {
      const data = await fetchSharedResult(shareIdInput);
      onLoadShared(data);
    } catch (error: unknown) {
      const notFound = error instanceof ApiError && error.status === 404;
      message.error(t(notFound ? 'idea_calc.history.shared_not_found' : 'idea_calc.history.shared_load_error'));
    } finally {
      setLoadingShared(false);
    }
  };

  const formatDate = (ts: string) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          {t('idea_calc.history.search_label')}
        </Typography.Text>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('idea_calc.history.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Введённое — не имя из своей истории, а share-id или ссылка на чужой расчёт:
            кнопка активна, только когда ввод разбирается как id. */}
        <Button
          size="small"
          icon={<LinkOutlined />}
          style={{ marginTop: 8 }}
          disabled={!shareIdInput}
          loading={loadingShared}
          onClick={handleLoadShared}
        >
          {t('idea_calc.history.load_by_share_button')}
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : groups.length === 0 ? (
        <Empty description={t('idea_calc.history.empty_state')} />
      ) : filteredGroups.length === 0 ? (
        <Empty description={t('idea_calc.history.no_matches')} />
      ) : mode === 'table' ? (
        <HistoryTable rows={tableRows} images={shownImages} onLoad={handleLoad} resultHref={resultHref} />
      ) : (
        <>
        <Collapse
          accordion={!query}
          {...(query ? { activeKey: visibleGroups.map((g) => g.asin) } : {})}
          items={visibleGroups.map((group) => ({
            key: group.asin,
            // Стрелка раскрытия — по центру миниатюры, а не у верхнего края строки.
            styles: { header: { alignItems: 'center' } },
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <ProductThumb src={shownImages[group.asin]} size={THUMB_SIZE} alt={group.asin} />
                <span>
                  <Typography.Text strong>{exchangeXidFromKey(group.asin) ? t('idea_calc.exchange.card_tag', { xid: exchangeXidFromKey(group.asin) }) : group.asin}</Typography.Text>
                  <Tag style={{ marginLeft: 8 }}>{t('idea_calc.history.versions_count', { count: group.versions_count })}</Tag>
                </span>
              </span>
            ),
            children: (
              <List
                size="small"
                dataSource={group.versions}
                renderItem={(v) => (
                  <List.Item
                    actions={[
                      <Button
                        key="load"
                        size="small"
                        type="primary"
                        ghost
                        icon={<FolderOpenOutlined />}
                        onClick={() => handleLoad(v.fileName)}
                      >
                        {t('idea_calc.history.load_button')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          {v.fileName}
                          {v.source === 'mcp' && (
                            <Tag color="purple" title={t('idea_calc.common.source_mcp_tooltip')}>
                              {t('idea_calc.common.source_mcp')}
                            </Tag>
                          )}
                        </span>
                      }
                      description={
                        <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span><ClockCircleOutlined /> {formatDate(v.timestamp)}</span>
                          {v.selling_price != null && (
                            <span><DollarOutlined /> ${v.selling_price}</span>
                          )}
                          {v.monthly_sales != null && (
                            <span><ShoppingCartOutlined /> {t('idea_calc.history.monthly_sales_short', { count: v.monthly_sales })}</span>
                          )}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            ),
          }))}
        />
        {hiddenCount > 0 && (
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 12 }}>
            {t('idea_calc.history.truncated_hint', { shown: visibleGroups.length, total: filteredGroups.length })}
          </Typography.Text>
        )}
        </>
      )}
    </>
  );
};

interface HistoryProps {
  open: boolean;
  onClose: () => void;
  onLoadResult: (fileName: string) => void;
  onLoadShared: (data: Record<string, unknown>) => void;
}

const LIST_WIDTH = 480;

/**
 * История шторкой — для standalone-шапки, где модуль владеет всей страницей. Внутри
 * платформы история живёт на своей вкладке (`HistoryPage`), а не поверх расчёта.
 */
const History: React.FC<HistoryProps> = ({ open, onClose, onLoadResult, onLoadShared }) => {
  const [mode, setMode] = useState<HistoryMode>('list');
  return (
    <Drawer
      title={<HistoryHeading mode={mode} onModeChange={setMode} />}
      placement="right"
      // Таблице нужна вся ширина, поэтому в этом режиме шторка раскрывается.
      styles={{ wrapper: { width: mode === 'table' ? '100%' : LIST_WIDTH } }}
      open={open}
      onClose={onClose}
    >
      <HistoryPanel
        active={open}
        mode={mode}
        onLoadResult={(fileName) => {
          onLoadResult(fileName);
          onClose();
        }}
        onLoadShared={(data) => {
          onLoadShared(data);
          onClose();
        }}
      />
    </Drawer>
  );
};

export default History;
